import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Card,
  Highlight,
  HighlightType,
  HighlightVariant,
  Icon,
  Tooltip,
} from '@node-space/ui-components'
import { EarnEmbeddedLabelValueRow } from './EarnEmbeddedLabelValueRow'

export interface EarnEmbeddedDetailsMerchantSummaryProps {
  earnRate: string
  allocatedFunds: string
  totalEarnings: string
  allocationEarnings: string
  customerEarnings: string
}

export const EarnEmbeddedDetailsMerchantSummary = ({
  earnRate,
  allocatedFunds,
  totalEarnings,
  allocationEarnings,
  customerEarnings,
}: EarnEmbeddedDetailsMerchantSummaryProps) => {
  const { t } = useTranslation()

  return (
    <Card
      variant="static"
      full
      borderRadius={12}
      testId="earn-details-merchant-summary"
      className="bg-surface-interactive"
    >
      <Box flexCol gap={4}>
        <Box flex gap={12} align="center">
          <Box className="text-foreground-action-secondary">
            <Icon name="CoinArrowInCustom" size="large" />
          </Box>
          <p className="flex-1 text-body-regular text-foreground-subdued">
            {t('earn.myAllocatedFunds')}
          </p>
          <Tooltip bodyContent={t('earn.myAllocatedFundsTooltip')} type="complex" position="bottom">
            <Button variant="ghost" noPadding iconElement={<Icon name="Info" />} />
          </Tooltip>
        </Box>

        <Box flex gap={8} align="center">
          <h1 className="text-h1 text-foreground-prominent">{allocatedFunds}</h1>
          <Highlight type={HighlightType.Feature} variant={HighlightVariant.Emphasis}>
            {t('earn.earningAtRate', { rate: earnRate })}
          </Highlight>
        </Box>

        <Box className="w-full border-t border-stroke-static mt-2 mb-2" />

        <Box flexCol gap={4}>
          <EarnEmbeddedLabelValueRow
            testId="earn-total-all-time-earnings"
            label={t('earn.myTotalAllTimeEarnings')}
            value={totalEarnings}
            labelVariant="emphasis"
            valueVariant="success"
          />
          <EarnEmbeddedLabelValueRow
            testId="earn-from-my-allocation"
            label={t('earn.fromMyAllocation')}
            value={allocationEarnings}
          />
          <EarnEmbeddedLabelValueRow
            testId="earn-from-customers"
            label={t('earn.fromCustomers')}
            value={customerEarnings}
          />
        </Box>
      </Box>
    </Card>
  )
}
